define([
	"dojo/dom-class",
	"csi/action",
	"csi/api",
	"csi/view",
	"csi/xml"
], function (domClass, csiAction, api, csiView, xml) {

	csiAction.registerControllers("", {
		ForceComplete: "Workflow.UTCController"
	});

	// link only when have a workflow execution
	api.addLinkValidation("WbsElement", ({ WorkflowExecution }) => xml.isValidURL(WorkflowExecution));

	return {
		// WBS and WBS element states
		stateBlocked: "Blocked",
		stateComplete: "Complete",
		stateInProcess: "InProcess",
		statePlanned: "Planned",
		statePlannedUnneeded: "PlannedUnneeded",
		stateReplanning: "Replanning",
		stateSkippable: "Skippable",
		stateSkipped: "Skipped",
		stateStartable: "Startable",
		stateSuspended: "Suspended",
		stateSuspendedInProcess: "SuspendedInProcess",
		stateSuspendedSkippable: "SuspendedSkippable",
		stateSuspendedStartable: "SuspendedStartable",
		stateUnneeded: "Unneeded",

		// WBS element modes
		modeManual: "Manual",
		modeTracking: "Tracking",

		suspendCannotChangeStructure: {
			"Complete": true,
			"InProcess": true,
			"Skipped": true,
			"SuspendedInProcess": true,
			"SuspendedSkippable": true,
			"SuspendedStartable": true,
			"Unneeded": true
		},

		otherTableViewId: {
			"WbsAttributes": "WbsInstanceElements",
			"WbsInstanceElements": "WbsAttributes",
			"WbsTemplateAttributes": "WbsTemplateElements",
			"WbsTemplateElements": "WbsTemplateAttributes"
		},

		// Functions
		getState: function (node) {
			return node ? xml.enumGetText(node.WEState || node.WState) : null;
		},

		getMode: function (node) {
			return node ? xml.enumGetText(node.Mode) : null;
		},

		isCriticalPath: function (wbsElement) {
			var floatDays = xml.nodeGetAttributeValue(wbsElement, "FloatDays");
			if (floatDays > 0)
				return false;

			var state = this.getState(wbsElement);
			return state == this.statePlanned || state == this.stateBlocked || state == this.stateInProcess;
		},

		onDrawFloatDays: function (/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			if (this.isCriticalPath(ctx.getNode())) {
				domClass.add(refNode, 'critical-path');
			}
		},

		onAccessActionInstancePredecessors: function (/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			var wbsState = this.getState(ctx.cache.getChildNode(node, "WBS"));
			var elemState = this.getState(node);
			return elemState == this.statePlanned || (this.suspendedOrReplanning(wbsState) && !this.suspendCannotChangeStructure[elemState]);
		},

		onAccessActionInstanceDeleteElement: function (/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			var elemState = this.getState(node);
			var wbsState = this.getState(ctx.cache.getChildNode(node, "WBS"));
			return wbsState == this.statePlanned || (this.suspendedOrReplanning(wbsState) && !this.suspendCannotChangeStructure[elemState]);
		},

		onAccessActionInstanceEditElement: function (/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			if (node.Elements.length)
				return false;

			var elemState = this.getState(node);
			var wbsState = this.getState(ctx.cache.getChildNode(node, "WBS"));
			return wbsState == this.statePlanned || (this.suspendedOrReplanning(wbsState) && !this.suspendCannotChangeStructure[elemState]);
		},

		onAccessActionInstanceNewElement: function (/*Context*/ctx, /*Action*/action) {
			var wbs = ctx.getParent();
			if (xml.nodeGetType(wbs) != "Wbs")
				wbs = ctx.cache.getChildNode(wbs, "WBS");
			var wbsState = this.getState(wbs);
			return wbsState == this.statePlanned || this.suspendedOrReplanning(wbsState);
		},

		onAccessActionInstanceMoveElement: function (/*Context*/ctx, /*Action*/action) {
			var wbs = ctx.getNode();
			if (xml.nodeGetType(wbs) != "Wbs")
				wbs = ctx.cache.getChildNode(wbs, "WBS");
			var wbsState = this.getState(wbs);
			return wbsState != this.stateComplete;
		},

		onAccessActionMarkAsUnneeded: function (/*Context*/ctx, /*Action*/action) {
			var state = this.getState(ctx.getNode());
			return state == this.statePlanned || state == this.stateBlocked || state == this.stateStartable || state == this.stateSuspendedStartable;
		},

		onAccessActionMarkAsNeeded: function (/*Context*/ctx, /*Action*/action) {
			var state = this.getState(ctx.getNode());
			return state == this.statePlannedUnneeded || state == this.stateUnneeded || state == this.stateSkippable || state == this.stateSuspendedSkippable;
		},

		onAccessActionMarkForRedo: function (/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			var elemState = this.getState(node);
			var wbsState = this.getState(ctx.cache.getChildNode(node, "WBS"));
			return wbsState == this.stateReplanning
				&& (elemState == this.stateComplete || elemState == this.stateSkipped || elemState == this.stateSuspendedInProcess);
		},

		onAccessActionForceStart: function (/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			var elemState = this.getState(node);
			var wbsState = this.getState(ctx.cache.getChildNode(node, "WBS"));
			return wbsState == this.stateInProcess
				&& (elemState == this.stateStartable || elemState == this.stateBlocked);
		},

		onAccessActionForceComplete: function (/*Context*/ctx, /*Action*/action) {
			var node = ctx.getNode();
			var elemMode = this.getMode(node);
			var elemState = this.getState(node);
			var wbsState = this.getState(ctx.cache.getChildNode(node, "WBS"));
			
			// Check if the workflow definition is a Camunda process definition
			const workflowDefinitionId = xml.nodeGetAttributeValue(node, "WorkflowDefinition");
			const workflowDefinitionNode = ctx.cache.getNode(workflowDefinitionId);
			const isCamundaWorkflow = xml.nodeGetType(workflowDefinitionNode) === "CamundaProcessDefinition";

			// Return whether force complete is allowed based on all conditions
			return elemMode !== this.modeTracking && wbsState === this.stateInProcess && elemState === this.stateInProcess && !isCamundaWorkflow;
		},

		refreshBothTables: function (ac) {
			this._refreshOtherTable(ac);
			return ac.widget.redrawData();
		},

		refreshAndCollapseBothTables: function (ac) {
			this._refreshOtherTable(ac);
			return ac.widget.postActionRedrawNode(ac);
		},

		_refreshOtherTable: function (ac) {
			var viewId = ac.widget.defView.getId();
			var otherViewId = this.otherTableViewId[viewId];

			if (otherViewId) {
				var otherView = csiView.getView(otherViewId);
				if (otherView) {
					otherView.redrawData().then(() => {
						otherView.toolbarNode.enablePlugins();
					})
				}
			}
		},

		refreshMilestoneTemplate: function (ac) {
			var view = csiView.getView("WbsSettings");
			if (!view) // called from Home / WBS Templates tab
				return;

			var heading = view.defView.findHeading("MilestoneTemplates");
			this._cacheForRefresh(ac, view);
			view.refreshHeadingStore(heading);
			view.refreshHeading(heading);
		},

		_cacheForRefresh: function (ac, view) {
			if (!view)
				return;

			if (ac.m_response) {
				view.cacheView.cacheResponse(ac.m_response);
			} else {
				view.cacheView.put(ac.getNode());
			}
		},

		refreshTemplatePreviewViews: function (ac) {
			var gantt = csiView.getView("Gantt");
			if (gantt) {
				gantt.cacheView.cacheResponse(ac.m_response);
				gantt.refreshWidget(true);
			}
			return this.refreshBothTables(ac);
		},

		suspendedOrReplanning: function (wbsState) {
			return wbsState == this.stateSuspended || wbsState == this.stateReplanning;
		}
	};
});